import { type ChangeEvent, type ChangeEventHandler, type HTMLAttributes, type ReactNode, type Ref, useCallback, useId } from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { clsx } from 'clsx';
import { HorizontalStack, type HorizontalStackProps } from '../../../alignment/horizontal-stack';
import { VerticalStack } from '../../../alignment/vertical-stack';
import { Typography } from '../../../typography';
import { ErrorMessage } from '../../error-message/error-message';
import { InputHint } from '../../input-hint/input-hint';
import { Label } from '../../label/label';
import type { RadioOption } from '../../radio-group-input/radio-group-input';
import inputStyles from '../../input.module.scss';
import toggleStyles from '../fancy-toggle.module.scss';
import styles from './fancy-radio-group-input.module.scss';

type FancyRadioGroupInputSize = 'small' | 'medium' | 'large';

export interface FancyRadioGroupOption<T extends string = string> extends RadioOption<T> {
  affixIcon?: ReactNode;
  affixIconPosition?: 'left' | 'right';
  subTitle?: ReactNode;
}

export interface FancyRadioGroupInputProps<T extends string = string> extends Omit<HTMLAttributes<HTMLInputElement>, 'type'> {
  disabled?: boolean;
  error?: string;
  gap?: HorizontalStackProps['gap'];
  hideLabel?: boolean;
  hint?: ReactNode;
  label: string;
  name: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  options: FancyRadioGroupOption<T>[];
  ref?: Ref<HTMLDivElement>;
  required?: boolean;
  size?: FancyRadioGroupInputSize;
  value: string;
}

export function FancyRadioGroupInput<T extends string = string>({
  className,
  disabled,
  error,
  hideLabel,
  hint,
  id: providedId,
  label,
  name,
  onChange,
  options,
  ref,
  required,
  size = 'medium',
  gap = size === 'large' ? 4 : 2,
  value,
  ...rest
}: FancyRadioGroupInputProps<T>) {
  const generatedId = useId();
  const id = providedId || generatedId;
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const showError = Boolean(error);
  const handleChange = useCallback(
    (newValue: string) => {
      if (onChange) {
        onChange({ target: { value: newValue, name } } as ChangeEvent<HTMLInputElement>);
      }
    },
    [onChange, name],
  );

  const renderedOptions = options.map((option) => {
    const optionId = `${id}-${option.value}`;
    const affixIconPosition = option.affixIconPosition ?? 'left';
    const hasSubtitle = option.subTitle !== undefined && option.subTitle !== null;
    const leadingIcon = option.affixIcon && affixIconPosition === 'left' ? option.affixIcon : null;
    const trailingIcon = option.affixIcon && affixIconPosition === 'right' ? option.affixIcon : null;
    const subtitle = hasSubtitle ? (
      <Typography lineHeight="1.125rem" size="0.875rem" weight="regular">
        {option.subTitle || <span>&zwnj;</span>}
      </Typography>
    ) : null;

    return (
      <RadioGroupPrimitive.Item asChild disabled={option.disabled} id={optionId} key={option.value} value={option.value}>
        <button
          className={clsx(
            toggleStyles.button,
            toggleStyles[`size-${size}`],
            hasSubtitle && toggleStyles.withSubtitle,
            value === option.value && toggleStyles.isActive,
            option.affixIcon && toggleStyles.hasIcon,
            toggleStyles[`icon-position-${affixIconPosition}`],
          )}
          type="button"
        >
          {size === 'large' && hasSubtitle && leadingIcon ? (
            <HorizontalStack align="start" blockAlign="start" fullWidth gap={2} wrap="nowrap">
              <span className={styles.leadingIcon}>{leadingIcon}</span>
              <VerticalStack className={styles.optionCopy} gap={2} inlineAlign="start">
                <span>{option.label}</span>
                {subtitle}
              </VerticalStack>
            </HorizontalStack>
          ) : (
            <VerticalStack align={size === 'large' ? undefined : 'center'} gap={2} inlineAlign={size === 'large' && hasSubtitle ? 'start' : 'center'}>
              <HorizontalStack align="center" blockAlign="center" gap={size === 'large' ? 2 : 1} wrap="nowrap">
                {leadingIcon}
                <span>{option.label}</span>
                {trailingIcon}
              </HorizontalStack>

              {subtitle}
            </VerticalStack>
          )}
        </button>
      </RadioGroupPrimitive.Item>
    );
  });

  return (
    <RadioGroupPrimitive.Root
      aria-describedby={hint ? hintId : undefined}
      aria-errormessage={showError ? errorId : undefined}
      asChild
      id={id}
      onValueChange={handleChange}
      ref={ref}
      value={value}
      {...(rest as RadioGroupPrimitive.RadioGroupProps)}
    >
      <VerticalStack
        as="fieldset"
        className={clsx(
          styles.fancyRadioGroupInput,
          toggleStyles.fancyToggle,
          inputStyles.input,
          disabled && inputStyles.isDisabled,
          toggleStyles[`size-${size}`],
          className,
        )}
        gap={2}
      >
        {!hideLabel && <Label as="legend" className={styles.label} hasError={showError} isFixed label={label} required={required} />}

        {size === 'large' ? (
          <VerticalStack className={styles.groupContainer} gap={gap}>
            {renderedOptions}
          </VerticalStack>
        ) : (
          <HorizontalStack blockAlign="center" className={styles.groupContainer} gap={gap}>
            {renderedOptions}
          </HorizontalStack>
        )}

        {hint ? <InputHint id={hintId}>{hint}</InputHint> : null}
        {showError ? <ErrorMessage id={errorId}>{error}</ErrorMessage> : null}
      </VerticalStack>
    </RadioGroupPrimitive.Root>
  );
}
